/**
 * The client brief — ADR-001 §11.8 (`GET /clients/{id}/brief`), guide §5.3; prototype `VIEWS.cusage` (L4145,
 * the brief became a page) and the Team → Briefs tab (L3659).
 *
 * One payload, so the brief opens in one round trip. It is the ADR's shape — client, health, products, open
 * work, last touchpoints, engagement, targets, revenue — carrying the prototype's sections ("Where we stand",
 * warnings, usage by product, engagement this month, what is open, ideas to lift adoption, copy as a WhatsApp
 * update). Health, touchpoints and targets are in the ADR and guide but not drawn by the prototype: they are
 * served so the screen can show them; the UI decides placement.
 *
 * Revenue: `revenue` is present for managers only. The member schema is strict, so a leaked `revenue` key
 * fails the API's own response validation (§11.1 field masking). Tone reasons follow GET /clients/{id}/health.
 */
import { z } from 'zod';
import { IsoDate, Lakh, Timestamp, Uuid, list } from './common.js';
import { ClientContact, ClientHealth, ClientHealthMember, ClientRef, Segment } from './clients.js';
import { EngagementCell } from './engagement.js';
import { ClientTarget } from './targets.js';
import { Alert, ClientProductUsage } from './usage.js';
import { UserRef } from './users.js';
/** An idea to lift adoption (prototype `briefFacts`): per-product tips, plus relationship and engagement ideas. */
export const BriefIdea = z
    .object({
    /** The product name, or "Relationship" / "Engagement" for the two extra ideas. */
    topic: z.string(),
    product_key: z.string().nullable(),
    kind: z.enum(['low', 'fall', 'high', 'relationship', 'engagement']),
    text: z.string(),
})
    .meta({ id: 'BriefIdea' });
/** A recent contact with the client. Meetings and emails join in step 8; until then logs and notes. */
export const Touchpoint = z
    .object({
    at: Timestamp,
    kind: z.enum(['engagement', 'meeting', 'email_in', 'email_out', 'whatsapp', 'note']),
    /** One line: "Client meeting logged", "Email from Priya: Renewal terms". */
    label: z.string(),
    by: UserRef.nullable(),
    route: z.string().nullable(),
})
    .meta({ id: 'Touchpoint' });
const BriefBase = z.object({
    as_of: IsoDate,
    client: ClientRef,
    since: IsoDate,
    is_new_client: z.boolean(),
    /** Latest health snapshot (score, band, reasons); `null` until one exists. */
    health: ClientHealth.nullable(),
    /** "Where we stand." — "2 warnings on Meridian. Usage is 22% below …", or "Nothing to warn about. …". */
    where_we_stand: z.string(),
    /** The compact line (prototype `briefLine`): products, engagement, escalations, overdue. */
    line: z.string(),
    /** Open usage alerts for the client, worst first (the warnings list). */
    warnings: z.array(Alert),
    /** Usage by product, as on the client usage page. */
    products: z.array(ClientProductUsage),
    /** Engagement this month: the eight cells. */
    engagement: z.array(EngagementCell),
    engagement_summary: z.object({
        total: z.number().int().nonnegative(),
        on_track: z.number().int().nonnegative(),
        overdue: z.number().int().nonnegative(),
        /** Names of the overdue activities, in order ("overdue: Training, MIS with next steps"). */
        overdue_names: z.array(z.string()),
    }),
    /** What is open. */
    open_work_summary: z.object({
        tasks: z.number().int().nonnegative(),
        overdue_tasks: z.number().int().nonnegative(),
        escalations: z.number().int().nonnegative(),
        client_requests: z.number().int().nonnegative(),
        /** "Last heard N days ago"; `null` when no contact is recorded. */
        last_heard_days: z.number().int().nonnegative().nullable(),
        /** The primary contact. */
        contact: ClientContact.nullable(),
    }),
    /** Ideas to lift adoption; empty prints "Nothing needs changing. Keep the rhythm going." */
    ideas: z.array(BriefIdea),
    /** The latest five, newest first. */
    last_touchpoints: z.array(Touchpoint).max(5),
    /** This quarter's targets for the client. */
    targets: z.array(ClientTarget),
    /** "Copy as a WhatsApp update" — plain text, no revenue. */
    share_text: z.string(),
});
/** Managers: the header's "₹N L a year" and the two labelled revenue figures (operator decision 16). */
export const BriefRevenue = z
    .object({
    /** The founder's annual figure (authoritative for the Today tile until Zoho). */
    annual_revenue_lakh: Lakh.nullable(),
    /** Σ per-product MRR, per month. */
    mrr_lakh: Lakh,
    /** 12 × Σ MRR — what the prototype header prints. */
    annual_from_mrr_lakh: Lakh,
})
    .strict()
    .meta({ id: 'BriefRevenue' });
/** Without revenue, and without tone reasons in `health` (the two audiences are the same people: managers). */
export const ClientBriefMember = BriefBase.extend({ health: ClientHealthMember.nullable() }).strict().meta({ id: 'ClientBriefMember' });
export const ClientBriefManager = BriefBase.extend({ revenue: BriefRevenue }).strict().meta({ id: 'ClientBriefManager' });
export const ClientBrief = z.union([ClientBriefManager, ClientBriefMember]).meta({ id: 'ClientBrief' });
// ---------------------------------------------------------------- GET /briefs (Team → Briefs, managers)
export const BriefCard = z
    .object({
    client: ClientRef,
    line: z.string(),
    /** Per-product chips "{name} {util}%". */
    products: z.array(z.object({ product_key: z.string(), name: z.string(), util: z.number().min(0).max(400).nullable() })),
    /** Chip "Engagement {on_track}/{total}". */
    engagement: z.object({ on_track: z.number().int().nonnegative(), total: z.number().int().nonnegative() }),
    /** "Next: {first idea}"; `null` when there is nothing to change. */
    next: z.string().nullable(),
    band: ClientHealth.shape.band.nullable(),
})
    .meta({ id: 'BriefCard' });
export const BriefCardList = list(BriefCard).extend({ as_of: IsoDate }).meta({ id: 'BriefCardList' });
export const ListBriefsQuery = z.object({ segment: Segment.optional(), spoc_id: Uuid.optional() });
//# sourceMappingURL=brief.js.map